import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, MessageCircle, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import api from "@/lib/api";

function formatTime(d) { try { return format(new Date(d), "d MMM HH:mm", { locale: fr }); } catch { return d; } }

export default function MessagesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => { loadConversations(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Deep-link: ?partner=<userId>&name=<name> opens (or creates) a conversation
  useEffect(() => {
    if (loading) return;
    const partnerId = searchParams.get("partner");
    if (!partnerId || selectedPartner?.partner_id === partnerId) return;
    const existing = conversations.find(c => c.partner_id === partnerId);
    const partner = existing || {
      partner_id: partnerId,
      partner_name: searchParams.get("name") || "Utilisateur",
      last_message: "",
      unread_count: 0,
    };
    openConversation(partner);
    // Clear the URL params once we've consumed them so back-button doesn't loop
    const next = new URLSearchParams(searchParams);
    next.delete("partner");
    next.delete("name");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [loading, conversations, searchParams]);

  const loadConversations = async () => {
    try { const { data } = await api.get("/messages/conversations"); setConversations(data); }
    catch (err) { console.error("Failed to load conversations:", err); }
    finally { setLoading(false); }
  };

  const openConversation = async (partner) => {
    setSelectedPartner(partner);
    try {
      const { data } = await api.get(`/messages/conversation/${partner.partner_id}`);
      setMessages(data);
      loadConversations();
    } catch (err) { console.error("Failed to open conversation:", err); toast.error("Impossible de charger les messages"); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !selectedPartner) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { receiver_id: selectedPartner.partner_id, content: newMsg.trim() });
      setMessages(prev => [...prev, data]);
      setNewMsg("");
      loadConversations();
    } catch (err) { console.error("Failed to send message:", err); toast.error("Envoi du message impossible"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 mb-6" data-testid="messages-title">Messages</h1>
      <Card className="border border-zinc-200 rounded-2xl overflow-hidden bg-white" style={{ height: "calc(100vh - 200px)", minHeight: "500px" }} data-testid="messages-card">
        <div className="flex h-full">
          {/* Conversation List */}
          <div className={`${selectedPartner ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 border-r border-zinc-200`}>
            <div className="p-4 border-b border-zinc-200">
              <h2 className="font-heading font-bold text-zinc-950">Conversations</h2>
            </div>
            <ScrollArea className="flex-1">
              {conversations.length === 0 ? (
                <div className="p-6 text-center" data-testid="no-conversations">
                  <MessageCircle className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">Aucune conversation</p>
                  <p className="text-zinc-400 text-xs mt-1">Contactez un propriétaire depuis la page d'un terrain</p>
                </div>
              ) : conversations.map((c, i) => (
                <button key={c.partner_id} onClick={() => openConversation(c)}
                  className={`w-full text-left p-4 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${selectedPartner?.partner_id === c.partner_id ? "bg-emerald-50" : ""}`}
                  data-testid={`conversation-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700 flex-shrink-0">
                      {c.partner_name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-zinc-950 text-sm truncate">{c.partner_name}</span>
                        {c.unread > 0 && <Badge className="bg-emerald-600 text-white border-0 text-xs ml-2">{c.unread}</Badge>}
                      </div>
                      <p className="text-xs text-zinc-500 truncate mt-0.5">{c.last_message}</p>
                      {c.space_title && <p className="text-xs text-emerald-600 truncate">{c.space_title}</p>}
                    </div>
                  </div>
                </button>
              ))}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`${selectedPartner ? "flex" : "hidden md:flex"} flex-col flex-1`}>
            {selectedPartner ? (
              <>
                <div className="p-4 border-b border-zinc-200 flex items-center gap-3">
                  <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => setSelectedPartner(null)} data-testid="back-to-list"><ArrowLeft className="h-4 w-4" /></Button>
                  <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700">
                    {selectedPartner.partner_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div><p className="font-medium text-zinc-950 text-sm">{selectedPartner.partner_name}</p>
                    {selectedPartner.space_title && <p className="text-xs text-emerald-600">{selectedPartner.space_title}</p>}
                  </div>
                </div>
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.map((m, i) => {
                      const isMine = m.sender_id === user?._id || m.sender_id === user?.id;
                      return (
                        <div key={m.id || i} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`message-${i}`}>
                          <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${isMine ? "bg-emerald-600 text-white rounded-br-md" : "bg-zinc-100 text-zinc-900 rounded-bl-md"}`}>
                            <p>{m.content}</p>
                            <p className={`text-xs mt-1 ${isMine ? "text-emerald-200" : "text-zinc-400"}`}>{formatTime(m.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                <form onSubmit={sendMessage} className="p-4 border-t border-zinc-200 flex gap-2" data-testid="message-form">
                  <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Ecrivez votre message..." className="rounded-xl" disabled={sending} data-testid="message-input" />
                  <Button type="submit" disabled={!newMsg.trim() || sending} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="send-message-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-6">
                <div><MessageCircle className="h-12 w-12 text-zinc-300 mx-auto mb-3" /><p className="text-zinc-500">Sélectionnez une conversation</p></div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
